/* eslint-disable @typescript-eslint/no-explicit-any */
import axios from 'axios';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { getPlanInfo, monthlyEquivalent } from '../planUtils';
import styles from './index.module.scss';

/**
 * Summary cards above the integrations / subscriptions table.
 *
 * All values come from Outseta:
 *  - Monthly total  = sum of each company's monthly-equivalent plan rate
 *  - Next invoice   = earliest upcoming renewal date across companies
 *  - Payment method = read from the Outseta account's PaymentInformation
 *    (fetched client-side, same pattern as BuySubscriptionModal). Outseta does
 *    NOT document card brand/last-4 (cards live in Stripe), so we deep-scan
 *    PaymentInformation for a last-4 / brand and fall back to "Managed in
 *    Outseta" when it isn't present. "Update" opens the Outseta customer portal.
 */

// Deep, case-insensitive scan for card brand + last-4 in an unknown object.
const extractCard = (obj: any): { brand?: string; last4?: string } | null => {
	if (!obj || typeof obj !== 'object') return null;
	let last4: string | undefined;
	let brand: string | undefined;

	const visit = (node: any, depth: number) => {
		if (!node || typeof node !== 'object' || depth > 4) return;
		for (const key of Object.keys(node)) {
			const value = node[key];
			const k = key.toLowerCase();
			if (
				!last4 &&
				/(last_?4|lastfour|cardlast|masked|last4digits)/.test(k) &&
				(typeof value === 'string' || typeof value === 'number')
			) {
				const digits = String(value).replace(/\D/g, '');
				if (digits.length >= 4) last4 = digits.slice(-4);
			}
			if (
				!brand &&
				/(cardtype|card_type|cardbrand|brand)/.test(k) &&
				typeof value === 'string' &&
				value.trim()
			) {
				brand = value.trim();
			}
			if (value && typeof value === 'object') visit(value, depth + 1);
		}
	};

	visit(obj, 0);
	return last4 ? { brand, last4 } : null;
};

const SubscriptionSummary = (props: any) => {
	const { data } = props;

	const admins = (data || []).filter((row: any) => row?.isCompanyAdmin);
	const plans = admins.map((row: any) => getPlanInfo(row?.subscription));

	// Monthly total (round to a clean figure)
	const monthlyTotal = plans.reduce(
		(sum: number, info: any) => sum + monthlyEquivalent(info),
		0
	);
	const monthlyTotalLabel = `$${Math.round(monthlyTotal)}/mo`;

	// Next invoice = earliest future renewal date we have
	const now = moment();
	const upcoming = plans
		.map((p: any) => p.renewalMoment)
		.filter((m: any) => m && m.isValid())
		.sort((a: any, b: any) => a.valueOf() - b.valueOf());
	const nextRenewal =
		upcoming.find((m: any) => m.isSameOrAfter(now, 'day')) || upcoming[0];
	const nextInvoiceLabel = nextRenewal ? nextRenewal.format('MMM D, YYYY') : '—';

	// Payment method from Outseta account PaymentInformation (best-effort).
	const [paymentMethod, setPaymentMethod] = useState<string>(
		'Managed in Outseta'
	);

	const accountUid =
		admins
			.map((row: any) => row?.subscription?.zohoSubscriptionPlan?.Account?.Uid)
			.find((uid: any) => !!uid) || null;

	useEffect(() => {
		const base = process.env.REACT_APP_OUTSETA_BASE_URL;
		const key = process.env.REACT_APP_OUTSETA_API_KEY;
		const secret = process.env.REACT_APP_OUTSETA_API_SECRET;
		if (!base || !key || !secret || !accountUid) return;

		let cancelled = false;
		(async () => {
			try {
				const res = await axios.get(
					`${base}/crm/accounts/${accountUid}?fields=*,PaymentInformation.*`,
					{
						headers: {
							Authorization: `Outseta ${key}:${secret}`,
							'Content-Type': 'application/json',
						},
					}
				);
				const card = extractCard(res?.data?.PaymentInformation);
				if (!cancelled && card?.last4) {
					const brand = card.brand || 'Card';
					setPaymentMethod(`${brand} •••• ${card.last4}`);
				}
			} catch (err) {
				// Outseta doesn't reliably expose card data — keep the fallback.
				console.error('Failed to load payment information', err);
			}
		})();

		return () => {
			cancelled = true;
		};
	}, [accountUid]);

	const openPortal = () => {
		const url = process.env.REACT_APP_CUSTOMER_PORTAL_LINK;
		if (url) {
			window.open(url, '_blank');
		}
	};

	return (
		<div className={styles['summary']}>
			<div className={styles['summary__card']}>
				<p className={styles['summary__label']}>Monthly total</p>
				<p className={styles['summary__value']}>{monthlyTotalLabel}</p>
			</div>

			<div className={styles['summary__card']}>
				<p className={styles['summary__label']}>Next invoice</p>
				<p className={styles['summary__value']}>{nextInvoiceLabel}</p>
			</div>

			<div className={styles['summary__card']}>
				<div>
					<p className={styles['summary__label']}>Payment method</p>
					<p className={styles['summary__value']}>{paymentMethod}</p>
				</div>
				<span className={styles['summary__update']} onClick={openPortal}>
					Update
				</span>
			</div>
		</div>
	);
};

export default SubscriptionSummary;
